import { useEffect, useMemo, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import Markdown from 'react-markdown'
import { toast } from 'sonner'

import { RichTextareaWithSubmit } from '@/components/composer/RichTextareaWithSubmit'
import { CreateEmailTemplateButton } from '@/components/CreateEmailTemplateButton'
import { EmailTemplateSelectItems } from '@/components/EmailTemplateSelectItems'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select'
import { isProduction } from '@/config/config'
import { parseError } from '@/lib/errors'
import { DocumentType } from '@/models/document'
import { type EmailTemplate } from '@/models/emailTemplate'
import { type SalesLead, useSendSalesLeadEmail } from '@/models/salesLead'
import { useCurrentUser } from '@/models/user'
import { leadContactEmails } from './contactEmails'
import { buildLeadEmailBody } from './leadEmailDefaults'
import { RecipientSelect } from './RecipientSelect'

interface SendEmailFormProps {
  lead: SalesLead
  templates: EmailTemplate[]
  defaultTemplateId: string | null
  signature: string
  // Initially selected recipient (from the Email button's picker). Seeds the
  // recipient picker; the user can add or remove addresses before sending.
  recipientEmail?: string | null
  initialSubject?: string
  initialBody?: string
  inReplyToActivityId?: string
  hideSubject?: boolean
  resetKey?: number | string
  onSent?: () => void
  onCancel?: () => void
}

function firstName(contactName: string): string {
  return contactName.trim().split(/\s+/)[0] || 'there'
}

function templateById(templates: EmailTemplate[], id: string | null): EmailTemplate | null {
  return templates.find((t) => t.id === id) ?? null
}

function rendered(template: EmailTemplate | null, signature: string, driverName: string) {
  return {
    // No template (rep hasn't created any): start from an empty draft.
    subject: template ? template.subject : '',
    body: buildLeadEmailBody({
      driverName,
      template: template?.body ?? '',
      signature,
    }),
  }
}

function initialSelectedId(templates: EmailTemplate[], defaultTemplateId: string | null): string | null {
  if (defaultTemplateId && templates.some((t) => t.id === defaultTemplateId)) {
    return defaultTemplateId
  }
  return templates[0]?.id ?? null
}

export function SendEmailForm({
  lead,
  templates,
  defaultTemplateId,
  signature,
  recipientEmail,
  initialSubject,
  initialBody,
  inReplyToActivityId,
  hideSubject,
  resetKey,
  onSent,
  onCancel,
}: SendEmailFormProps) {
  const sendEmail = useSendSalesLeadEmail()
  const { user } = useCurrentUser({ enabled: true })

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(() =>
    initialSelectedId(templates, defaultTemplateId)
  )
  const primaryContact = lead.contacts.find((c) => c.isPrimary) ?? lead.contacts.at(0) ?? null
  const driverName = useMemo(() => firstName(primaryContact?.name ?? ''), [primaryContact?.name])
  const leadEmails = useMemo(() => leadContactEmails(lead.contacts), [lead.contacts])
  const initialRecipients = () => {
    const seed = recipientEmail ?? leadEmails[0]?.value
    return seed ? [seed] : []
  }
  const [recipients, setRecipients] = useState<string[]>(initialRecipients)

  const toggleRecipient = (email: string) => {
    setRecipients((prev) =>
      prev.some((e) => e.toLowerCase() === email.toLowerCase())
        ? prev.filter((e) => e.toLowerCase() !== email.toLowerCase())
        : [...prev, email]
    )
  }
  const initial = useMemo(() => {
    if (initialSubject !== undefined || initialBody !== undefined) {
      return { subject: initialSubject ?? '', body: initialBody ?? '' }
    }
    return rendered(templateById(templates, selectedTemplateId), signature, driverName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, signature, driverName, initialSubject, initialBody])

  const [subject, setSubject] = useState(initial.subject)
  const [content, setContent] = useState(initial.body)
  const [editorKey, setEditorKey] = useState(0)

  // Pending switch target (used by the warn-on-dirty confirm)
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null)

  const selectedTemplate = templateById(templates, selectedTemplateId)
  const dirty = subject !== initial.subject || content !== initial.body

  // resetKey-driven full reset (parent triggers when composer mode opens fresh)
  useEffect(() => {
    setRecipients(initialRecipients())
    if (initialSubject !== undefined || initialBody !== undefined) {
      setSubject(initialSubject ?? '')
      setContent(initialBody ?? '')
      setEditorKey((k) => k + 1)
      return
    }
    const newSelectedId = initialSelectedId(templates, defaultTemplateId)
    setSelectedTemplateId(newSelectedId)
    const r = rendered(templateById(templates, newSelectedId), signature, driverName)
    setSubject(r.subject)
    setContent(r.body)
    setEditorKey((k) => k + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  // External-change handling:
  // - Selected template deleted: switch dropdown to new default; preserve dirty draft.
  // - Selected template content changed AND form is clean: re-prefill silently.
  useEffect(() => {
    if (initialSubject !== undefined || initialBody !== undefined) return // override mode — don't auto-prefill from templates
    if (selectedTemplateId === null) return
    const stillExists = templates.some((t) => t.id === selectedTemplateId)
    if (!stillExists) {
      const newId = initialSelectedId(templates, defaultTemplateId)
      setSelectedTemplateId(newId)
      if (!dirty) {
        const r = rendered(templateById(templates, newId), signature, driverName)
        setSubject(r.subject)
        setContent(r.body)
        setEditorKey((k) => k + 1)
      }
      return
    }
    if (!dirty) {
      const r = rendered(selectedTemplate, signature, driverName)
      if (r.subject !== subject || r.body !== content) {
        setSubject(r.subject)
        setContent(r.body)
        setEditorKey((k) => k + 1)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, defaultTemplateId, signature])

  const canSend =
    subject.trim().length > 0 &&
    content.trim().length > 0 &&
    (hideSubject || recipients.length > 0) &&
    !sendEmail.isPending

  function applyTemplate(id: string | null) {
    setSelectedTemplateId(id)
    const r = rendered(templateById(templates, id), signature, driverName)
    setSubject(r.subject)
    setContent(r.body)
    setEditorKey((k) => k + 1)
  }

  function handleTemplateChange(newId: string) {
    if (newId === selectedTemplateId) return
    if (dirty) {
      setPendingTemplateId(newId)
      return
    }
    applyTemplate(newId)
  }

  function confirmSwap() {
    if (pendingTemplateId === null) return
    applyTemplate(pendingTemplateId)
    setPendingTemplateId(null)
  }

  function cancelSwap() {
    setPendingTemplateId(null)
  }

  function handleSend(documentIds: string[]) {
    if (!canSend) return
    const htmlContent = renderToStaticMarkup(<Markdown>{content}</Markdown>)
    // Only attribute a template when one actually drove the draft (not reply /
    // override composes). `initial.body` is the pristine rendered template the
    // user started from — the backend measures edit % against it.
    const templateActive = initialSubject === undefined && initialBody === undefined
    sendEmail.mutate(
      {
        id: lead.id,
        subject,
        content,
        htmlContent,
        to: hideSubject ? undefined : recipients,
        inReplyToActivityId,
        attachmentDocumentIds: documentIds.length > 0 ? documentIds : undefined,
        templateId: templateActive ? selectedTemplateId : undefined,
        templateBaseline: templateActive ? initial.body : undefined,
      },
      {
        onSuccess: () => {
          toast.success('Email sent')
          onSent?.()
        },
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  const pendingTemplateName =
    pendingTemplateId !== null
      ? templates.find((t) => t.id === pendingTemplateId)?.displayName || 'Untitled'
      : null

  return (
    <div className="flex flex-col gap-3">
      {!isProduction && !hideSubject && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Dev mode: email will be sent to{' '}
          <span className="font-mono font-medium">{user?.email ?? 'your address'}</span>, not the lead's
          address.
        </div>
      )}

      {!hideSubject && (
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">To:</span>
            <RecipientSelect
              options={leadEmails}
              selected={recipients}
              onToggle={toggleRecipient}
              disabled={sendEmail.isPending}
              className="w-fit max-w-full text-sm"
            />
          </div>
          {leadEmails.length === 0 && recipients.length === 0 && (
            <p className="mt-1 text-xs text-red-600">No email on file — add a recipient to send.</p>
          )}
        </div>
      )}

      {!hideSubject && initialSubject === undefined && initialBody === undefined && (
        <div>
          <label className="mb-1 block text-sm font-medium">Template</label>
          {templates.length > 0 && selectedTemplateId !== null ? (
            <Select
              value={selectedTemplateId}
              onValueChange={handleTemplateChange}
              disabled={sendEmail.isPending}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent>
                <EmailTemplateSelectItems templates={templates} />
              </SelectContent>
            </Select>
          ) : (
            <CreateEmailTemplateButton />
          )}
        </div>
      )}

      {!hideSubject && (
        <div>
          <label className="mb-1 block text-sm font-medium">Subject</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
      )}

      <div>
        {!hideSubject && <label className="mb-1 block text-sm font-medium">Message</label>}
        <RichTextareaWithSubmit
          editorKey={editorKey}
          value={content}
          onValueChange={setContent}
          onSubmitMessage={({ documentIds }) => handleSend(documentIds)}
          buttonProps={{ disabled: !canSend }}
          enableUploads
          uploadDocumentType={DocumentType.EMAIL_ATTACHMENT}
          addonChildren={
            onCancel && (
              <Button variant="ghost" size="sm" onClick={onCancel}>
                Cancel
              </Button>
            )
          }
        />
      </div>

      <Dialog open={pendingTemplateId !== null} onOpenChange={(open) => !open && cancelSwap()}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Replace your current draft?</DialogTitle>
            <DialogDescription>
              Your edits will be replaced with template{' '}
              <span className="font-medium">&quot;{pendingTemplateName}&quot;</span>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelSwap}>
              Cancel
            </Button>
            <Button onClick={confirmSwap}>Replace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
